import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { smiUrl } from 'components';
import { catchError, Observable, of } from 'rxjs';
import { retryUnlessPlanRefusal } from '@brand-lolly/app/social-media-influencer/modules/social-listening/utils/social-listening-refusal.util';

export interface BrandListItem {
	id: string;
	name: string;
	status: string;
	/**
	 * When collection last produced anything, for the header's status block.
	 * Optional because the field is being added to this response separately —
	 * absent, the header says "Not collected yet" rather than nothing at all.
	 */
	last_collected_at?: string | null;
	/**
	 * Why the entity's last collection run collected nothing, when it was
	 * stopped: `entity_budget`, `account_pool`, `credit_balance`,
	 * `free_ceiling` or `daily_allowance`. Null or absent once a run reserves.
	 */
	last_run_stopped_by?: string | null;
}

@Injectable({ providedIn: 'root' })
export class GetSocialListeningBrandListService {
	constructor(private http: HttpClient) {}

	/**
	 * The user's brands, or none.
	 *
	 * This used to answer any failed request with three invented brands —
	 * "Brand 1", "Brand 2", "Brand 3", ids '1' to '3' — and every page showed
	 * them in its brand selector as if they were real: a report could be
	 * created for one. A deploy restarting the API for a second was enough.
	 * It retries a transient failure first, the way the nav service's own copy
	 * already did, and if the list still cannot be read it says there is
	 * none rather than making some up.
	 */
	fetch(): Observable<BrandListItem[]> {
		return this.read().pipe(catchError(() => of<BrandListItem[]>([])));
	}

	/**
	 * {@link fetch} without the empty fallback: a failure that survives the
	 * retries reaches the caller. For the one caller that must tell "no
	 * brands" from "refused for the plan" — the redirect guard, which would
	 * otherwise send a brand without the plan into the setup wizard.
	 */
	read(): Observable<BrandListItem[]> {
		const url = smiUrl()
			.add('social-listening')
			.add('brand')
			.add('list')
			.done();

		return this.http
			.get<BrandListItem[]>(url)
			.pipe(retryUnlessPlanRefusal());
	}
}
